import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import NetsuiteIntegrationForm, {
    type NetsuiteFormData,
    type NetsuiteIntegration,
} from './NetsuiteIntegrationForm.tsx';
import Button from '../../../core/components/Button.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_NETSUITE_PATH } from '../../constants.ts';
import { GET_NETSUITE_INTEGRATIONS, UPDATE_NETSUITE_INTEGRATION } from '../../queries.ts';

const FORM_ID = 'netsuite-integration-form';

const NetsuiteIntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const navigate = useNavigate();
    const { publishToast } = useToaster();

    const backTooltipText = `${t('backTooltipText')} ${t('netsuite.title')}`;

    const { data, loading, refetch } = useQuery(GET_NETSUITE_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration = data?.netsuiteIntegrations?.find((i) => i.id === integrationId);

    const [updateIntegration, { loading: isSubmitting }] = useMutation(UPDATE_NETSUITE_INTEGRATION);

    const handleUpdateIntegration = async (
        formData: NetsuiteFormData,
        onSuccess?: (updated: NetsuiteIntegration) => void
    ) => {
        if (!integration) return;

        try {
            const result = await updateIntegration({
                variables: {
                    id: integration.id,
                    name: formData.name,
                    clientId: formData.clientId,
                    clientSecret: formData.clientSecret,
                    tokenId: formData.tokenId,
                    tokenSecret: formData.tokenSecret,
                    accountId: formData.accountId,
                    instanceUrl: formData.instanceUrl,
                    restServiceUrl: formData.restServiceUrl,
                    orderFileCabinetFolderId: formData.orderFileCabinetFolderId,
                    orderCustomFormId: formData.orderCustomFormId,
                    orderChannels: formData.orderChannelIds,
                    rfqFileCabinetFolderId: formData.rfqFileCabinetFolderId,
                    rfqCustomFormId: formData.rfqCustomFormId,
                    rfqChannels: formData.rfqChannelIds,
                    universalFileCabinetFolderId: formData.universalFileCabinetFolderId,
                    universalChannels: formData.universalChannelIds,
                    masterDataMode: formData.masterDataMode,
                    invoiceFileCabinetFolderId: formData.invoiceFileCabinetFolderId,
                    invoiceCustomFormId: formData.invoiceCustomFormId,
                    invoiceChannels: formData.invoiceChannelIds,
                    defaultApprovalStatus: formData.defaultApprovalStatus,
                    defaultExpenseAccountId: formData.defaultExpenseAccountId,
                    defaultSubsidiaryId: formData.defaultSubsidiaryId,
                    defaultLocationId: formData.defaultLocationId,
                    useAltaviaMapping: formData.useAltaviaMapping,
                    altaviaRestletUrl: formData.altaviaRestletUrl,
                },
            });

            await refetch();
            const updatedIntegration = result.data?.updateNetsuiteIntegration?.integration;
            if (onSuccess && updatedIntegration) onSuccess(updatedIntegration);

            publishToast({ description: t('netsuite.updateSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('netsuite.updateError'), status: 'error' });
        }
    };

    if (!loading && !integration) {
        return (
            <Layout>
                <SettingsNavigation />
                <Page className="flex-1">
                    <Page.Content lowered>
                        <p className="text-content-medium">{t('netsuite.noIntegrations')}</p>
                        <Button type="button" asChild className="mt-4">
                            <Link to={url(CUSTOMER_INTEGRATION_NETSUITE_PATH)}>{t('netsuite.form.cancelButton')}</Link>
                        </Button>
                    </Page.Content>
                </Page>
            </Layout>
        );
    }

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('netsuite.title'), to: url(CUSTOMER_INTEGRATION_NETSUITE_PATH) },
                            { label: integration?.name || t('netsuite.form.updateTitle') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Button type="button" asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_NETSUITE_PATH)}>{t('netsuite.form.cancelButton')}</Link>
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            form={FORM_ID}
                            loading={isSubmitting}
                            disabled={loading || !integration}
                        >
                            {t('netsuite.form.updateButton')}
                        </Button>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    {!loading && integration && (
                        <NetsuiteIntegrationForm
                            integration={integration}
                            onSubmit={handleUpdateIntegration}
                            formId={FORM_ID}
                        />
                    )}
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default NetsuiteIntegrationEditPage;
